// src/pages/Subscribe/SubscribePage.js
import React, { useEffect, useState } from "react";
import { Button, Row, Col, Spinner } from "react-bootstrap";
import { Auth } from "aws-amplify";
import {
  getSubscriptionStatus,
  startCheckout,
  openBillingPortal,
} from "../../lib/subscription";
import { useNavigate } from "react-router-dom";
import "./SubscribePage.css";
import FloatingPawButton from "../../components/FloatingPawButton";

function SubscribePage() {
  const [authChecked, setAuthChecked] = useState(false);
  const [isAuthed, setIsAuthed] = useState(false);

  const [subLoading, setSubLoading] = useState(false);
  const [subStatus, setSubStatus] = useState("none"); // "none" | "active" | "canceled"...
  const [planName, setPlanName] = useState(null);
  const [petLimit, setPetLimit] = useState(null);

  const [tagId, setTagId] = useState("");

  const navigate = useNavigate();

  const price2Pets = process.env.REACT_APP_PRICE_2_PETS;
  const price5Pets = process.env.REACT_APP_PRICE_5_PETS;
  const priceUnlimited = process.env.REACT_APP_PRICE_10_PETS;

  const benefits = [
    "Health AI assistant",
    "Behavior AI coaching",
    "Document storage",
    "Vet reminders",
    "Lost pet alert system",

    "AI Generated Feeding plans",
    "Pet profile",
    "Personalized nutrition & care advice",
    "Symptom checking guidance",
    "24/7 AI support",
  ];

  const SubscriptionBenefits = () => (
    <div className="subscribe-benefits-card mt-4">
      <h5 className="mb-2">Your subscription includes:</h5>
      <p className="text-muted small mb-3">
        We’re rolling these out in phases. Some features will start simple and
        grow more powerful over time — all included in your plan.
      </p>
      <div className="row">
        {benefits.map((item) => (
          <div
            key={item}
            className="col-12 col-sm-6 mb-2 d-flex align-items-start"
          >
            <span className="me-2 mt-1">✅</span>
            <span className="small">{item}</span>
          </div>
        ))}
      </div>
    </div>
  );

  // 1️⃣ Figure out if user is logged in
  useEffect(() => {
    (async () => {
      try {
        await Auth.currentAuthenticatedUser();
        setIsAuthed(true);
      } catch {
        setIsAuthed(false);
      } finally {
        setAuthChecked(true);
      }
    })();
  }, []);

  // 2️⃣ Try to resolve a tagId from querystring or localStorage
  useEffect(() => {
    if (typeof window === "undefined") return;

    const params = new URLSearchParams(window.location.search);
    const queryTag = params.get("tag");

    const storedTag = window.localStorage
      ? window.localStorage.getItem("lpp.tagId")
      : "";

    const finalTag = (queryTag || storedTag || "").toUpperCase();
    setTagId(finalTag);
  }, []);

  // 3️⃣ If logged in, check subscription status
  useEffect(() => {
    if (!isAuthed) return;

    (async () => {
      setSubLoading(true);
      try {
        const { status, planName: pName, petLimit: pLimit } =
          await getSubscriptionStatus();
        setSubStatus(status || "none");
        setPlanName(pName || null);
        setPetLimit(pLimit ?? null);
      } catch (err) {
        console.error("Failed to load subscription status:", err);
        setSubStatus("none");
        setPlanName(null);
        setPetLimit(null);
      } finally {
        setSubLoading(false);
      }
    })();
  }, [isAuthed]);

  // 🧠 Decide header title/subtitle + main content
  let headerTitle = "SmartTag Subscription";
  let headerSubtitle = "Activate or manage your Little Paws SmartTag plan.";
  let content = null;

  if (!authChecked) {
    headerSubtitle = "We’re checking your account details…";
    content = (
      <p className="text-muted text-center mt-3">
        Checking your account…
      </p>
    );
  } else if (!isAuthed) {
    if (tagId) {
      headerTitle = "Connect Your SmartTag";
      headerSubtitle =
        "Create your free Little Paws Place™ account to activate your tag.";
      content = (
        <div className="d-flex flex-column align-items-center text-center">
          <h2 className="mb-3">Connect Your SmartTag</h2>
          <p className="text-muted mb-3" style={{ maxWidth: 520 }}>
            We found SmartTag <strong>{tagId}</strong> in your link.
            <br />
            Create your free Little Paws Place™ account to activate it and
            unlock AI-powered tools for your pet.
          </p>
          <a href="/login" className="btn btn-primary">
            Sign in or create an account
          </a>
          <SubscriptionBenefits />
        </div>
      );
     } else {
    headerTitle = "Get Your Little Paws SmartTag";
    headerSubtitle =
      "Order a SmartTag from Little Paws Place to unlock AI safety, health, and recovery tools.";
    content = (
      <div className="d-flex flex-column align-items-center text-center">
        <h2 className="mb-3">Get Your Little Paws SmartTag</h2>
        <p className="text-muted mb-3" style={{ maxWidth: 520 }}>
          To activate SmartTag AI features, you’ll first need a physical
          SmartTag for your pet. Once your tag arrives, you’ll scan it to
          create your account and add a subscription.
        </p>

        {/* 🔁 Updated CTA: send them to your own site’s hero instead of Amazon */}
        <Button
          className="lpp-pill-btn"
          type="button"
          onClick={() => navigate("/")}
        >
          Buy a SmartTag on our site
        </Button>

        <SubscriptionBenefits />
      </div>
    );
  }
}
 else if (subLoading) {
    headerSubtitle = "We’re checking your current plan…";
    content = (
      <p className="text-muted text-center mt-3">
        Checking your subscription…
      </p>
    );
  } else if (subStatus === "active") {
    headerTitle = "Your SmartTag subscription is active";
    headerSubtitle = planName
      ? `Current plan: ${planName}`
      : "You already have an active subscription.";

    content = (
      <div className="d-flex flex-column align-items-center text-center">
        <h2 className="mb-2">Your SmartTag subscription is active 🎉</h2>

        {planName && (
          <p className="mb-2">
            Current plan: <strong>{planName}</strong>
            {petLimit && petLimit < 9999 && (
              <>
                {" "}
                &mdash; covers up to <strong>{petLimit}</strong> pets in your
                household.
              </>
            )}
            {petLimit && petLimit >= 9999 && (
              <> &mdash; covers your entire household.</>
            )}
          </p>
        )}

        {tagId && (
          <p className="text-muted" style={{ maxWidth: 480 }}>
            You’re currently using SmartTag <strong>{tagId}</strong>. If you
            need more SmartTags or a higher pet limit, you can upgrade your
            plan below.
          </p>
        )}

        {!tagId && (
          <p className="text-muted" style={{ maxWidth: 480 }}>
            If you need more SmartTags or a higher pet limit, you can upgrade
            your plan below.
          </p>
        )}

        <div className="d-flex flex-wrap gap-2 mt-3 justify-content-center">
          <Button
            className="lpp-pill-btn"
             size="sm"
            onClick={openBillingPortal}
          >
            Manage or upgrade plan
          </Button>

       {tagId && (
  <Button
    className="lpp-pill-btn-outline"
    size="sm"
    onClick={() => navigate("/smarttag")}
  >
    Back to SmartTag
  </Button>
)}


          {!tagId && (
            <Button
              className="lpp-pill-btn-outline"
              type="button"
              onClick={() => navigate("/")}
            >
              Back to home
            </Button>
          )}
        </div>

        <small className="text-muted mt-3">
          Upgrades and billing changes are handled securely in your Stripe
          billing portal.
        </small>
      </div>
    );
  } else {
    // 🟢 Logged in, no active subscription → show tiered plans + benefits
    headerTitle = "Choose your household plan";
    headerSubtitle =
      "One subscription powers SmartTag AI and safety tools for your pets.";

    content = (
      <div className="d-flex flex-column align-items-center">
        <h2 className="mb-3">Choose your household plan</h2>
        <p
          className="text-muted mb-4 text-center"
          style={{ maxWidth: 520 }}
        >
          Your subscription unlocks SmartTag AI and tools to keep your pets
          safe, healthy, and easy to recover. One plan covers all SmartTags
          under your account.
        </p>

        <div className="subscribe-plans-row d-flex gap-3 flex-wrap justify-content-center">
          {/* Starter – 2 pets */}
          <div className="card p-3" style={{ minWidth: 220 }}>
            <h4 className="mb-1">Starter</h4>
            <p className="mb-1">$4.99 / month</p>
            <p className="small text-muted mb-2">Up to 2 pets</p>
            <Button
               type="button"
              className="lpp-pill-btn"
              onClick={() => startCheckout(price2Pets)}
            >
              Continue with Starter
            </Button>
          </div>

          {/* Family – 5 pets */}
          <div className="card p-3 border-success" style={{ minWidth: 220 }}>
            <h4 className="mb-1">Family</h4>
            <p className="mb-1">$7.99 / month</p>
            <p className="small text-muted mb-1">Up to 5 pets</p>
            <small className="text-success d-block mb-2">Most popular</small>
            <Button
              className="lpp-pill-btn"
              onClick={() => startCheckout(price5Pets)}
            >
              Continue with Family
            </Button>
          </div>

          {/* Unlimited – household */}
          <div className="card p-3" style={{ minWidth: 220 }}>
            <h4 className="mb-1">Unlimited</h4>
            <p className="mb-1">$9.99 / month</p>
            <p className="small text-muted mb-2">Entire household</p>
            <Button
              className="lpp-pill-btn-outline"
              onClick={() => startCheckout(priceUnlimited)}
            >
              Continue with Unlimited
            </Button>
          </div>
        </div>

        <SubscriptionBenefits />

        <small className="text-muted mt-3">
          You can manage or cancel your subscription anytime in your billing
          portal.
        </small>
      </div>
    );
  }

  const showStatusPill = authChecked && isAuthed && !subLoading;

  return (
    <div className="subscribe-wrapper">
      <div className="subscribe-shell">
        {/* Gradient header card, consistent with other pages */}


        {/* Body content (centered, matches 820px width) */}
        <div className="subscribe-body">{content}</div>
      </div>
       <div >
          <FloatingPawButton isAuthenticated={true} hasActiveSub={true} />
        </div>
    </div>
  );
}

export default SubscribePage;
